import { useFetchValidationSchema } from 'app/utils/hooks';
import { MutableRefObject, useEffect, useMemo } from 'react';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { errorMessages } from 'app/components/SignUp/utils';
import { PasswordPolicy } from 'types/auth';
import * as yup from 'yup';
import { fieldValidationMessages } from 'app/constants';
import { useLocation } from 'react-router-dom';
import { Link } from 'types/api.types';
import { routes } from 'app/routing/routesConfig';
import { SignUpFormStep } from 'types/enums';
import {
  getUserDataFromQuery,
  validateReferralParam,
} from 'app/components/SignUp/SignUpForm/utils';
import { Option } from 'types/general.types';
import { UseFormSetValue } from 'react-hook-form';
import { SignUpFormFields } from 'app/components/SignUp/SignUpForm/types';
import { setReferralAction } from 'app/store/Mixpanel/slice';
import { useAppDispatch } from 'app/store';
import { signUpFormDefaultValues } from './constants';

export const useSignUpFormValidations = ({
  signUpLink,
  passwordPolicy,
}: {
  signUpLink?: Link;
  passwordPolicy?: PasswordPolicy;
}) => {
  const { validationSchema } = useFetchValidationSchema({ link: signUpLink });

  const yupValidationSchema = useMemo(
    () => {
      if (validationSchema) {
        delete validationSchema.properties.password;
      }
      return transformSchemaToYup(
        validationSchema,
        signUpFormDefaultValues,
        errorMessages
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationSchema, errorMessages]
  );

  const concatenatedSchema = yup
    .object()
    .shape({
      password: yup
        .string()
        .required(fieldValidationMessages.REQUIRED_PASSWORD)
        .min(
          passwordPolicy?.minimumLength || 6,
          fieldValidationMessages.MIN_PASSWORD
        )
        .max(
          passwordPolicy?.maximumLength || 30,
          fieldValidationMessages.MAX_CHAR(passwordPolicy?.maximumLength || 30)
        )
        .matches(
          new RegExp(passwordPolicy?.pattern || /.*/),
          fieldValidationMessages.WEAK_PASSWORD
        ),
      country: yup
        .object()
        .required(fieldValidationMessages.REQUIRED)
        .nullable(),
      passwordRepeat: yup
        .string()
        .required(fieldValidationMessages.PASSWORD_REPEAT)
        .oneOf(
          [yup.ref('password'), null],
          errorMessages.errors.passwordRepeat.match
        ),
      persona: yup.string().required(fieldValidationMessages.REQUIRED),
    })
    .concat(yupValidationSchema);

  return {
    concatenatedSchema,
    referralValidationSchema: validationSchema?.properties?.referral,
  };
};

export const useQueryParams = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

interface UseSignUpFormStepsProps {
  pathname: string;
  setFormStep: (state: SignUpFormStep) => void;
  isBasicFormValid: boolean;
}

export const useSignUpFormSteps = ({
  setFormStep,
  pathname,
  isBasicFormValid,
}: UseSignUpFormStepsProps) => {
  useEffect(() => {
    if (pathname.includes(routes.signUpBasic.path)) {
      setFormStep(SignUpFormStep.BASIC);
    }
    if (pathname.includes(routes.signUpDetails.path)) {
      setFormStep(
        isBasicFormValid ? SignUpFormStep.DETAILS : SignUpFormStep.BASIC
      );
    }
    if (pathname.includes(routes.signUpPersona.path)) {
      setFormStep(SignUpFormStep.USER_PERSONA);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
};

interface UseCountryQueryProps {
  countryOptions: Option[];
  pathname: string;
  setValue: UseFormSetValue<SignUpFormFields>;
  values: SignUpFormFields;
}

export const useCountryQuery = ({
  countryOptions,
  pathname,
  setValue,
  values,
}: UseCountryQueryProps) => {
  const query = useQueryParams();

  useEffect(() => {
    if (
      countryOptions?.length &&
      pathname.includes(routes.signUpDetails.path)
    ) {
      const { country } = getUserDataFromQuery(query, countryOptions);
      if (country && !values.country) {
        setValue('country', country);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions, pathname]);
};

interface UseReferralParamProps {
  referralValidationSchema: { pattern: string };
  referral: MutableRefObject<string | undefined>;
}

export const useReferralParam = ({
  referral,
  referralValidationSchema,
}: UseReferralParamProps) => {
  const dispatch = useAppDispatch();
  const query = useQueryParams();

  useEffect(() => {
    if (referralValidationSchema) {
      // eslint-disable-next-line no-param-reassign
      referral.current = validateReferralParam(
        query,
        referralValidationSchema.pattern
      );
      const referralName = query.get('referralName') || 'soundcloud';
      dispatch(
        setReferralAction({
          referral: referral.current,
          referralOrigin: referralName,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, query, referralValidationSchema]);
};
